import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Target, Users, Gauge, ShieldCheck, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BENEFITS = [
  { icon: Target, title: "In-market buyers", desc: "Reach decision-makers actively researching solutions like yours." },
  { icon: Users, title: "Full buying committee", desc: "Engage every stakeholder influencing the enterprise purchase." },
  { icon: Gauge, title: "Pipeline, not vanity", desc: "Programs optimised for qualified opportunities and revenue." },
  { icon: ShieldCheck, title: "Compliant by design", desc: "Enterprise-grade consent and data handling on every lead." },
];

const empty = { name: "", email: "", company: "", job_title: "", phone: "", interest: "", message: "" };

const LeadGeneration = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Name, work email and company are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "lead-generation" });
      toast.success("Thanks! Our growth team will reach out shortly.");
      setForm(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="lead-generation-page">
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="absolute -top-16 -right-10 h-80 w-80 rounded-full bg-electric/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="overline-label text-electric">B2B Lead Generation</span>
            <h1 className="mt-3 font-display text-4xl lg:text-6xl font-extrabold leading-tight">
              Qualified opportunities, delivered
            </h1>
            <p className="mt-5 text-white/75 text-lg max-w-xl">
              Tap into the Lead ITES audience of technology and business decision-makers. Launch
              demand programs that turn insight into pipeline.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex gap-3" data-testid={`benefit-${b.title.replace(/\s/g, "-").toLowerCase()}`}>
                  <b.icon className="h-5 w-5 text-electric shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{b.title}</p>
                    <p className="text-sm text-white/60">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-xl">
            <h2 className="font-display text-2xl font-extrabold text-navy">Get started</h2>
            <p className="mt-2 text-sm text-muted-foreground">Tell us your goals — we'll design a program that fits.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="leadgen-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Full name *</Label><Input value={form.name} onChange={set("name")} data-testid="leadgen-name" /></div>
                <div className="space-y-1.5"><Label>Company *</Label><Input value={form.company} onChange={set("company")} data-testid="leadgen-company" /></div>
              </div>
              <div className="space-y-1.5"><Label>Work email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="leadgen-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Job title</Label><Input value={form.job_title} onChange={set("job_title")} data-testid="leadgen-title" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="leadgen-phone" /></div>
              </div>
              <div className="space-y-1.5"><Label>Your goals</Label><Textarea rows={3} value={form.message} onChange={set("message")} data-testid="leadgen-message" /></div>
              <Button type="submit" disabled={loading} className="btn-electric w-full rounded-full" data-testid="leadgen-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request Program"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16">
        <div className="rounded-2xl border border-border bg-secondary p-10 text-center">
          <h2 className="font-display text-2xl font-extrabold text-navy">Trusted by growth-focused teams</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-10 text-navy/70">
            {["Enterprise-grade consent", "Verified business contacts", "Transparent attribution", "Dedicated strategist"].map((t) => (
              <span key={t} className="flex items-center gap-2 font-medium"><Check className="h-4 w-4 text-electric" /> {t}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadGeneration;
